import { mountImageBatch } from './image-batch-queue.js';
import { canvasToBlob, imageIsAr, imageName, makeCanvas, openBitmap } from './image-batch-utils.js';
import { trackEvent } from '../core/analytics.js';

const t = (ar, en) => imageIsAr ? ar : en;

let pointerDrag = null;
let currentCropMetrics = null;

function calculateCrop(srcW, srcH, settings) {
  const ratioVal = settings.ratio || 'original';
  const ratio = ratioVal === 'original'
    ? (srcW / srcH)
    : ratioVal.split(':').map(Number).reduce((a, b) => a / b);

  const sourceRatio = srcW / srcH;
  let maxW = srcW;
  let maxH = srcH;
  if (sourceRatio > ratio) {
    maxW = Math.round(srcH * ratio);
  } else {
    maxH = Math.round(srcW / ratio);
  }

  const scalePercent = Math.max(10, Math.min(100, Number(settings.cropScale ?? 100))) / 100;
  const cropW = Math.max(16, Math.round(maxW * scalePercent));
  const cropH = Math.max(16, Math.round(maxH * scalePercent));

  const maxCropX = Math.max(0, srcW - cropW);
  const maxCropY = Math.max(0, srcH - cropH);
  const focusX = Number(settings.focusX ?? 50);
  const focusY = Number(settings.focusY ?? 50);
  const cropX = Math.round(maxCropX * (focusX / 100));
  const cropY = Math.round(maxCropY * (focusY / 100));

  return {
    cropW,
    cropH,
    cropX,
    cropY,
    maxW,
    maxH,
    maxCropX,
    maxCropY,
    focusX,
    focusY,
    ratioVal,
    scalePercent
  };
}

function getHandleUnderPointer(px, py) {
  if (!currentCropMetrics) return null;
  const { dispX, dispY, dispW, dispH } = currentCropMetrics;
  const threshold = 16;
  if (Math.hypot(px - dispX, py - dispY) <= threshold) return 'tl';
  if (Math.hypot(px - (dispX + dispW), py - dispY) <= threshold) return 'tr';
  if (Math.hypot(px - dispX, py - (dispY + dispH)) <= threshold) return 'bl';
  if (Math.hypot(px - (dispX + dispW), py - (dispY + dispH)) <= threshold) return 'br';
  return null;
}

function isInsideCrop(px, py) {
  if (!currentCropMetrics) return false;
  const { dispX, dispY, dispW, dispH } = currentCropMetrics;
  return px >= dispX && px <= dispX + dispW && py >= dispY && py <= dispY + dispH;
}

const fields = [
  {
    key: 'ratio',
    label: t('نسبة الأبعاد', 'Aspect ratio'),
    type: 'segmented',
    span: 'full',
    default: 'original',
    options: [
      { value: 'original', label: t('الأصلية', 'Original') },
      { value: '16:9', label: '16:9 (Gaming Display)' },
      { value: '21:9', label: '21:9 (Ultrawide)' },
      { value: '32:9', label: '32:9 (Super Ultrawide)' },
      { value: '1:1', label: t('1:1 (أيقونة / ديسكورد)', '1:1 (Discord / Avatar)') },
      { value: '17:6', label: t('17:6 (بنر ديسكورد)', '17:6 (Discord Banner)') },
      { value: '5:2', label: t('5:2 (غلاف تويتش)', '5:2 (Twitch Banner)') },
      { value: '4:3', label: '4:3' },
      { value: '9:16', label: '9:16 (Shorts / TikTok)' }
    ]
  },
  {
    key: 'cropScale',
    label: t('حجم إطار القص (الزووم)', 'Crop frame zoom'),
    type: 'range',
    min: 20,
    max: 100,
    step: 1,
    default: 100,
    suffix: '%',
    hint: t('اسحب مقابض الزوايا على الصورة أو حرّك المؤشر لتصغير أو تكبير إطار القص', 'Drag corner handles on image or slide to resize crop frame')
  },
  {
    key: 'size',
    label: t('أطول ضلع (الدقة القصوى)', 'Long edge (Resolution)'),
    type: 'segmented',
    span: 'full',
    default: 'original',
    options: [
      { value: 'original', label: t('الأصلية (بدون تصغير)', 'Original (No resize)') },
      { value: '720', label: '720 px' },
      { value: '1280', label: '1280 px (HD)' },
      { value: '1920', label: '1920 px (FHD)' },
      { value: '2560', label: '2560 px (2K)' }
    ]
  },
  {
    key: 'format',
    label: t('صيغة الإخراج', 'Output format'),
    type: 'select',
    default: 'image/png',
    options: [
      { value: 'image/png', label: 'PNG' },
      { value: 'image/webp', label: 'WebP' },
      { value: 'image/jpeg', label: 'JPEG' }
    ]
  },
  {
    key: 'focusX',
    label: t('الموضع الأفقي (X)', 'Horizontal focus (X)'),
    type: 'range',
    min: 0,
    max: 100,
    step: 1,
    default: 50,
    suffix: '%'
  },
  {
    key: 'focusY',
    label: t('الموضع الرأسي (Y)', 'Vertical focus (Y)'),
    type: 'range',
    min: 0,
    max: 100,
    step: 1,
    default: 50,
    suffix: '%'
  }
];

mountImageBatch({
  rootId: 'cropper-app',
  fields,
  zipName: 'cropped-images.zip',
  dropTitle: t('اسحب الصور أو انقر لاختيارها من جهازك للقص', 'Drop images or click to choose for cropping'),
  dropSubtitle: t('بلا حدود لعدد الصور · قص فائق الدقة ومعالجة محلية 100%', 'Unlimited images · High precision cropping & 100% private'),
  renderPreview: ({ canvas, ctx, bitmap, settings, statsEl }) => {
    const srcW = bitmap.width;
    const srcH = bitmap.height;
    const crop = calculateCrop(srcW, srcH, settings);

    const containerW = canvas.parentElement?.clientWidth || 720;
    const maxW = Math.min(760, containerW - 24);
    const canvasW = Math.max(280, maxW);
    const canvasH = Math.max(120, Math.round(canvasW * (srcH / srcW)));

    canvas.width = canvasW;
    canvas.height = canvasH;

    const scaleX = canvasW / srcW;
    const scaleY = canvasH / srcH;
    const dispX = crop.cropX * scaleX;
    const dispY = crop.cropY * scaleY;
    const dispW = crop.cropW * scaleX;
    const dispH = crop.cropH * scaleY;

    currentCropMetrics = {
      canvasW,
      canvasH,
      scaleX,
      scaleY,
      dispX,
      dispY,
      dispW,
      dispH,
      crop,
      srcW,
      srcH
    };

    // 1. Crisp base image
    ctx.drawImage(bitmap, 0, 0, canvasW, canvasH);

    // 2. Dim cut regions outside crop window
    ctx.save();
    ctx.fillStyle = 'rgba(10, 15, 30, 0.65)';
    if (dispY > 0) ctx.fillRect(0, 0, canvasW, dispY);
    const bottomY = dispY + dispH;
    if (bottomY < canvasH) ctx.fillRect(0, bottomY, canvasW, canvasH - bottomY);
    if (dispX > 0) ctx.fillRect(0, dispY, dispX, dispH);
    const rightX = dispX + dispW;
    if (rightX < canvasW) ctx.fillRect(rightX, dispY, canvasW - rightX, dispH);
    ctx.restore();

    // 3. Rule of thirds dashed lines
    ctx.save();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(dispX + dispW / 3, dispY);
    ctx.lineTo(dispX + dispW / 3, dispY + dispH);
    ctx.moveTo(dispX + (dispW * 2) / 3, dispY);
    ctx.lineTo(dispX + (dispW * 2) / 3, dispY + dispH);
    ctx.moveTo(dispX, dispY + dispH / 3);
    ctx.lineTo(dispX + dispW, dispY + dispH / 3);
    ctx.moveTo(dispX, dispY + (dispH * 2) / 3);
    ctx.lineTo(dispX + dispW, dispY + (dispH * 2) / 3);
    ctx.stroke();
    ctx.restore();

    // 4. Accent border
    ctx.save();
    ctx.strokeStyle = '#2563eb';
    ctx.lineWidth = 2.5;
    ctx.strokeRect(dispX, dispY, dispW, dispH);

    // 5. Four corner handles
    ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
    ctx.shadowBlur = 4;
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#2563eb';
    ctx.lineWidth = 2;
    const hSize = 10;
    const corners = [
      [dispX, dispY],
      [dispX + dispW, dispY],
      [dispX, dispY + dispH],
      [dispX + dispW, dispY + dispH]
    ];
    for (const [cx, cy] of corners) {
      ctx.beginPath();
      ctx.rect(cx - hSize / 2, cy - hSize / 2, hSize, hSize);
      ctx.fill();
      ctx.stroke();
    }
    ctx.restore();

    // 6. Stats badge
    if (statsEl) {
      const isOrig = crop.ratioVal === 'original';
      const scalePct = Math.round(crop.scalePercent * 100);
      const zoomText = scalePct < 100 ? ` · ${scalePct}%` : '';
      const fmt = (settings.format || 'image/png').split('/')[1]?.toUpperCase() || 'PNG';
      statsEl.textContent = `${srcW} × ${srcH} px → ${crop.cropW} × ${crop.cropH} px (${isOrig ? t('الأصلية', 'Original') : crop.ratioVal}${zoomText} · ${fmt})`;
    }
  },
  onPreviewPointer: ({ stageWrap, canvasEl, getBitmap, setSetting, drawPreview }) => {
    const getCanvasPoint = (e) => {
      const rect = canvasEl.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
      const scale = canvasEl.width / rect.width;
      return {
        x: (e.clientX - rect.left) * scale,
        y: (e.clientY - rect.top) * scale
      };
    };

    stageWrap.addEventListener('pointermove', (e) => {
      if (pointerDrag) return;
      const pt = getCanvasPoint(e);
      const handle = getHandleUnderPointer(pt.x, pt.y);
      if (handle === 'tl' || handle === 'br') {
        stageWrap.style.cursor = 'nwse-resize';
      } else if (handle === 'tr' || handle === 'bl') {
        stageWrap.style.cursor = 'nesw-resize';
      } else if (isInsideCrop(pt.x, pt.y)) {
        stageWrap.style.cursor = 'grab';
      } else {
        stageWrap.style.cursor = 'crosshair';
      }
    });

    stageWrap.addEventListener('pointerdown', (e) => {
      const bitmap = getBitmap();
      if (!bitmap || !canvasEl || !currentCropMetrics) return;
      const pt = getCanvasPoint(e);
      const handle = getHandleUnderPointer(pt.x, pt.y);
      const { dispX, dispY, dispW, dispH, scaleX, scaleY, crop } = currentCropMetrics;

      if (handle) {
        pointerDrag = {
          mode: 'resize',
          handle,
          startX: e.clientX,
          startY: e.clientY,
          centerX: dispX + dispW / 2,
          centerY: dispY + dispH / 2,
          maxW: crop.maxW * scaleX,
          maxH: crop.maxH * scaleY,
          initScale: crop.scalePercent * 100
        };
        stageWrap.style.cursor = (handle === 'tl' || handle === 'br') ? 'nwse-resize' : 'nesw-resize';
      } else if (isInsideCrop(pt.x, pt.y)) {
        pointerDrag = {
          mode: 'pan',
          startX: e.clientX,
          startY: e.clientY,
          initFocusX: crop.focusX,
          initFocusY: crop.focusY,
          maxCropX: crop.maxCropX,
          maxCropY: crop.maxCropY,
          scaleX,
          scaleY
        };
        stageWrap.style.cursor = 'grabbing';
      } else {
        const clickSrcX = pt.x / scaleX;
        const clickSrcY = pt.y / scaleY;
        if (crop.maxCropX > 0) {
          const targetFocusX = Math.max(0, Math.min(100, Math.round(((clickSrcX - crop.cropW / 2) / crop.maxCropX) * 100)));
          setSetting('focusX', targetFocusX);
        }
        if (crop.maxCropY > 0) {
          const targetFocusY = Math.max(0, Math.min(100, Math.round(((clickSrcY - crop.cropH / 2) / crop.maxCropY) * 100)));
          setSetting('focusY', targetFocusY);
        }
        drawPreview();
        const updated = currentCropMetrics;
        pointerDrag = {
          mode: 'pan',
          startX: e.clientX,
          startY: e.clientY,
          initFocusX: updated.crop.focusX,
          initFocusY: updated.crop.focusY,
          maxCropX: updated.crop.maxCropX,
          maxCropY: updated.crop.maxCropY,
          scaleX: updated.scaleX,
          scaleY: updated.scaleY
        };
        stageWrap.style.cursor = 'grabbing';
      }

      stageWrap.setPointerCapture(e.pointerId);
      stageWrap.classList.add('is-dragging');
    });

    stageWrap.addEventListener('pointermove', (e) => {
      if (!pointerDrag) return;
      if (pointerDrag.mode === 'resize') {
        const pt = getCanvasPoint(e);
        const distFromCenterX = Math.abs(pt.x - pointerDrag.centerX);
        const distFromCenterY = Math.abs(pt.y - pointerDrag.centerY);
        const maxHalfW = Math.max(1, pointerDrag.maxW / 2);
        const maxHalfH = Math.max(1, pointerDrag.maxH / 2);
        const scaleFromDist = Math.max(distFromCenterX / maxHalfW, distFromCenterY / maxHalfH) * 100;
        const clampedScale = Math.max(20, Math.min(100, Math.round(scaleFromDist)));
        setSetting('cropScale', clampedScale);
      } else if (pointerDrag.mode === 'pan') {
        const dx = e.clientX - pointerDrag.startX;
        const dy = e.clientY - pointerDrag.startY;
        const srcDx = dx / pointerDrag.scaleX;
        const srcDy = dy / pointerDrag.scaleY;

        if (pointerDrag.maxCropX > 0) {
          const newX = Math.max(0, Math.min(100, Math.round(pointerDrag.initFocusX + (srcDx / pointerDrag.maxCropX) * 100)));
          setSetting('focusX', newX);
        }
        if (pointerDrag.maxCropY > 0) {
          const newY = Math.max(0, Math.min(100, Math.round(pointerDrag.initFocusY + (srcDy / pointerDrag.maxCropY) * 100)));
          setSetting('focusY', newY);
        }
      }
      drawPreview();
    });

    const stopDrag = () => {
      if (pointerDrag) {
        pointerDrag = null;
        stageWrap.classList.remove('is-dragging');
        stageWrap.style.cursor = '';
      }
    };
    stageWrap.addEventListener('pointerup', stopDrag);
    stageWrap.addEventListener('pointercancel', stopDrag);
  },
  process: async (file, settings, report) => {
    report(10, t('جارٍ فتح الصورة', 'Opening image'));
    const bitmap = await openBitmap(file);
    const ratioVal = settings.ratio || 'original';
    const ratio = ratioVal === 'original'
      ? (bitmap.width / bitmap.height)
      : ratioVal.split(':').map(Number).reduce((a, b) => a / b);

    const sourceRatio = bitmap.width / bitmap.height;
    let maxW = bitmap.width;
    let maxH = bitmap.height;
    if (sourceRatio > ratio) {
      maxW = Math.round(bitmap.height * ratio);
    } else {
      maxH = Math.round(bitmap.width / ratio);
    }

    const scalePercent = Math.max(10, Math.min(100, Number(settings.cropScale ?? 100))) / 100;
    const cropW = Math.max(1, Math.round(maxW * scalePercent));
    const cropH = Math.max(1, Math.round(maxH * scalePercent));

    const maxCropX = Math.max(0, bitmap.width - cropW);
    const maxCropY = Math.max(0, bitmap.height - cropH);
    const x = Math.round(maxCropX * Number(settings.focusX ?? 50) / 100);
    const y = Math.round(maxCropY * Number(settings.focusY ?? 50) / 100);

    const longEdge = settings.size === 'original' ? Infinity : Number(settings.size);
    const scale = Number.isFinite(longEdge) ? Math.min(1, longEdge / Math.max(cropW, cropH)) : 1;
    const width = Math.max(1, Math.round(cropW * scale));
    const height = Math.max(1, Math.round(cropH * scale));

    const targetMime = settings.format || 'image/png';
    const isJpeg = targetMime === 'image/jpeg';
    const { canvas, context } = makeCanvas(width, height, isJpeg ? '#ffffff' : null);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, x, y, cropW, cropH, 0, 0, width, height);
    bitmap.close();

    const ext = isJpeg ? 'jpg' : (targetMime === 'image/webp' ? 'webp' : 'png');
    const quality = targetMime === 'image/png' ? undefined : 1.0;
    report(75, t('جارٍ تصدير الصورة', 'Exporting image'));
    const blob = await canvasToBlob(canvas, targetMime, quality);
    report(100, t('اكتمل القص', 'Crop complete'));
    trackEvent('tool_completed', 'gaming-image-cropper', { width, height, ratio: ratioVal, format: ext });
    const finalFilename = `${imageName(file.name)}.${ext}`;
    return {
      blob,
      filename: finalFilename,
      details: `${width} × ${height} px · ${ratioVal} · ${ext.toUpperCase()}`
    };
  }
});
